import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, FileText, AlertCircle, CheckCircle, Clock, DollarSign, Calendar, Building2, TrendingDown } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HUTANG_CATEGORIES = [
  'Supplier/Vendor',
  'Bank Loan',
  'Staff Loan',
  'Equipment Lease',
  'Office Rental',
  'Utilities',
  'Tax Payable',
  'Other'
];

const HUTANG_STATUS = [
  { value: 'pending', label: 'Belum Bayar', color: 'bg-red-100 text-red-700' },
  { value: 'partial', label: 'Sebahagian', color: 'bg-amber-100 text-amber-700' },
  { value: 'paid', label: 'Selesai', color: 'bg-green-100 text-green-700' },
  { value: 'overdue', label: 'Tertunggak', color: 'bg-red-600 text-white' }
];

const formatRM = (amount) => `RM ${(amount || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const getTodayDate = () => new Date().toISOString().split('T')[0];

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

const HutangForm = ({ onSubmit, onClose, initialData }) => {
  const [formData, setFormData] = useState(initialData || {
    creditor_name: '',
    category: 'Supplier/Vendor',
    description: '',
    total_amount: '',
    paid_amount: '',
    due_date: getTodayDate(),
    invoice_date: getTodayDate(),
    invoice_no: '',
    status: 'pending',
    remarks: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const totalAmount = parseFloat(formData.total_amount) || 0;
    const paidAmount = parseFloat(formData.paid_amount) || 0;
    
    // Auto-calculate status based on payment
    let status = formData.status;
    if (paidAmount >= totalAmount && totalAmount > 0) {
      status = 'paid';
    } else if (paidAmount > 0) {
      status = 'partial';
    } else if (new Date(formData.due_date) < new Date()) {
      status = 'overdue';
    }
    
    onSubmit({
      ...formData,
      total_amount: totalAmount,
      paid_amount: paidAmount,
      balance: totalAmount - paidAmount,
      status
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Nama Pemiutang *</label>
        <input type="text" value={formData.creditor_name} onChange={(e) => setFormData({...formData, creditor_name: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required 
          placeholder="cth: ABC Supplier Sdn Bhd" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
          <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
            {HUTANG_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">No. Invoice</label>
          <input type="text" value={formData.invoice_no} onChange={(e) => setFormData({...formData, invoice_no: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" 
            placeholder="cth: INV-2024-001" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Keterangan</label>
        <input type="text" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" 
          placeholder="cth: Pembelian peralatan pejabat" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tarikh Invoice</label>
          <input type="date" value={formData.invoice_date} onChange={(e) => setFormData({...formData, invoice_date: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tarikh Akhir Bayar *</label>
          <input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah Hutang (RM) *</label>
          <input type="number" step="0.01" value={formData.total_amount} onChange={(e) => setFormData({...formData, total_amount: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" required placeholder="0.00" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Jumlah Dibayar (RM)</label>
          <input type="number" step="0.01" value={formData.paid_amount} onChange={(e) => setFormData({...formData, paid_amount: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" placeholder="0.00" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Catatan</label>
        <textarea value={formData.remarks} onChange={(e) => setFormData({...formData, remarks: e.target.value})}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" rows={2}
          placeholder="Catatan tambahan..." />
      </div>

      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
          Batal
        </button>
        <button type="submit" className="flex-1 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 flex items-center justify-center gap-2">
          <Save className="w-4 h-4" /> Simpan
        </button>
      </div>
    </form>
  );
};

const HutangPage = () => {
  const [hutangList, setHutangList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchHutang();
  }, []);

  const fetchHutang = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hutang`);
      setHutangList(res.data || []);
    } catch (error) {
      console.error('Error fetching hutang:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (data) => {
    try {
      if (editItem) {
        await axios.put(`${API_URL}/api/hutang/${editItem.id}`, data);
      } else {
        await axios.post(`${API_URL}/api/hutang`, data);
      }
      fetchHutang();
      setShowModal(false);
      setEditItem(null);
    } catch (error) {
      console.error('Error saving hutang:', error);
      alert('Gagal menyimpan data hutang');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Padam rekod hutang ini?')) return;
    try {
      await axios.delete(`${API_URL}/api/hutang/${id}`);
      fetchHutang();
    } catch (error) {
      console.error('Error deleting hutang:', error);
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowModal(true);
  };

  // Filter data
  const filteredData = hutangList.filter(item => {
    if (filterStatus && item.status !== filterStatus) return false;
    if (filterCategory && item.category !== filterCategory) return false;
    if (searchTerm && !item.creditor_name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Calculate summary
  const summary = {
    totalHutang: filteredData.reduce((sum, item) => sum + (item.total_amount || 0), 0),
    totalDibayar: filteredData.reduce((sum, item) => sum + (item.paid_amount || 0), 0),
    totalBaki: filteredData.reduce((sum, item) => sum + ((item.total_amount || 0) - (item.paid_amount || 0)), 0),
    overdue: filteredData.filter(item => item.status === 'overdue').length,
    pending: filteredData.filter(item => item.status === 'pending').length
  };

  const getStatusBadge = (status) => {
    const statusObj = HUTANG_STATUS.find(s => s.value === status);
    return statusObj ? (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusObj.color}`}>
        {statusObj.label}
      </span>
    ) : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hutang-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Senarai Hutang</h1>
          <p className="text-gray-500 text-sm">Pengurusan hutang dan akaun belum bayar syarikat</p>
        </div>
        <button onClick={() => { setEditItem(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800"
          data-testid="add-hutang-btn">
          <Plus className="w-4 h-4" /> Tambah Hutang
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <TrendingDown className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Jumlah Hutang</p>
              <p className="text-lg font-bold text-gray-800">{formatRM(summary.totalHutang)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Telah Dibayar</p>
              <p className="text-lg font-bold text-green-600">{formatRM(summary.totalDibayar)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Baki Hutang</p>
              <p className="text-lg font-bold text-amber-600">{formatRM(summary.totalBaki)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Tertunggak</p>
              <p className="text-lg font-bold text-red-600">{summary.overdue}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Belum Bayar</p>
              <p className="text-lg font-bold text-blue-600">{summary.pending}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-wrap gap-4">
          <input
            type="text"
            placeholder="Cari pemiutang..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 w-full sm:w-auto"
          />
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
            <option value="">Semua Status</option>
            {HUTANG_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
            <option value="">Semua Kategori</option>
            {HUTANG_CATEGORIES.map(cat => <option key={cat} value={cat}>{cat}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="hutang-table">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Pemiutang</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Kategori</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">No. Invoice</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Tarikh Akhir</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Jumlah</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Dibayar</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Baki</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Tindakan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    Tiada rekod hutang
                  </td>
                </tr>
              ) : (
                filteredData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50" data-testid={`hutang-row-${item.id}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-800">{item.creditor_name}</p>
                        {item.description && <p className="text-xs text-gray-500">{item.description}</p>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.category}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.invoice_no || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{item.due_date}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-gray-800">{formatRM(item.total_amount)}</td>
                    <td className="px-4 py-3 text-sm text-right text-green-600">{formatRM(item.paid_amount)}</td>
                    <td className="px-4 py-3 text-sm text-right font-medium text-amber-600">
                      {formatRM((item.total_amount || 0) - (item.paid_amount || 0))}
                    </td>
                    <td className="px-4 py-3 text-center">{getStatusBadge(item.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleEdit(item)} className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          data-testid={`edit-hutang-${item.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(item.id)} className="p-1 text-red-600 hover:bg-red-50 rounded"
                          data-testid={`delete-hutang-${item.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setEditItem(null); }} 
        title={editItem ? 'Edit Hutang' : 'Tambah Hutang Baru'}>
        <HutangForm onSubmit={handleSubmit} onClose={() => { setShowModal(false); setEditItem(null); }} initialData={editItem} />
      </Modal>
    </div>
  );
};

export default HutangPage;
